import React, { createContext, useContext, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import api from '../api/axios';

interface PermissionContextType {
    permissions: string[];
    loading: boolean;
    hasPermission: (resource: string, action: string) => boolean;
    canAccess: (resource: string) => boolean;
}

const PermissionContext = createContext<PermissionContextType | null>(null);

export function PermissionProvider({ children }: { children: ReactNode }) {
    const { user, isLoaded, isSignedIn } = useUser();

    // Fetch current user's permissions
    const { data: permissions = [], isLoading, error } = useQuery({
        queryKey: ['current-user-permissions', user?.id],
        queryFn: async () => {
            try {
                const response = await api.get<string[]>('/me/permissions');
                return response.data;
            } catch (err) {
                throw err;
            }
        },
        enabled: isLoaded && isSignedIn && !!user,
        staleTime: 5 * 60 * 1000, // 5 minutes (matches backend cache)
        retry: 1,
        retryDelay: 1000
    });

    const hasPermission = React.useCallback(
        (resource: string, action: string): boolean => {
            if (!permissions || permissions.length === 0) {
                return false;
            }

            const required = `${resource}.${action}`;

            // Exact match
            if (permissions.includes(required)) {
                return true;
            }

            // Wildcard: resource.* matches resource.action
            if (permissions.includes(`${resource}.*`)) {
                return true;
            }

            // Wildcard: *.action matches resource.action
            if (permissions.includes(`*.${action}`)) {
                return true;
            }

            // Super wildcard: *.*
            if (permissions.includes('*.*')) {
                return true;
            }

            return false;
        },
        [permissions]
    );

    const canAccess = React.useCallback(
        (resource: string): boolean => {
            if (!permissions || permissions.length === 0) return false;

            // Check if user has any permission for this resource
            return permissions.some(
                (p) =>
                    p.startsWith(`${resource}.`) ||
                    p === '*.*' ||
                    p.startsWith('*.')
            );
        },
        [permissions]
    );

    return (
        <PermissionContext.Provider
            value={{
                permissions,
                loading: isLoading,
                hasPermission,
                canAccess,
            }}
        >
            {children}
        </PermissionContext.Provider>
    );
}

export function usePermission() {
    const context = useContext(PermissionContext);
    if (!context) {
        throw new Error('usePermission must be used within PermissionProvider');
    }
    return context;
}
